import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, BookOpen, Search, Phone, Mail, MapPin, Calendar, Wallet, Edit3, X, CheckCircle2, ClipboardCheck } from "lucide-react";
import { api } from "../../lib/api";
import AdminAttendanceViewer from "./AdminAttendanceViewer";

/**
 * V25 Phase 4 — Admin: Ongoing Tuitions list + edit/pause/complete.
 */
export default function OngoingTuitionsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [editing, setEditing] = useState(null);
    const [viewingAttendance, setViewingAttendance] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            const { data } = await api.get(`/admin/ongoing-tuitions?${params.toString()}`);
            setItems(data.items || []);
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <div className="space-y-4" data-testid="admin-ongoing-tuitions-panel">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-bold text-[#0F172A]">Ongoing Tuitions</h2>
                </div>
                <div className="text-sm text-slate-500">{items.length} record{items.length === 1 ? "" : "s"}</div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                        data-testid="ongoing-search" placeholder="Search student / parent / tutor / phone"
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
                <select value={status} onChange={(e) => { setStatus(e.target.value); }} data-testid="ongoing-status-filter"
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
                <button onClick={load} data-testid="ongoing-refresh" className="px-3 py-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold rounded-lg">Search</button>
            </div>

            {loading ? (
                <div className="text-center py-10 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Loading…</div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm">
                    No ongoing tuitions yet. Approve a successful demo from Pipeline / Leads to create one.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map((o) => (
                        <div key={o.id} data-testid={`ongoing-card-${o.id}`}
                            className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="font-bold text-[#0F172A]">{o.student_name}</div>
                                    <div className="text-xs text-slate-500">{o.grade} · {o.board} · {o.subjects}</div>
                                </div>
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${o.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : o.status === "paused" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>{o.status}</span>
                            </div>
                            <div className="space-y-1 text-xs text-slate-600">
                                <div><strong>Tutor:</strong> {o.tutor_name}</div>
                                <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {o.parent_phone}</div>
                                <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {o.parent_email || "—"}</div>
                                <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.area}, {o.city}, {o.pin_code}</div>
                                <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {o.schedule}</div>
                                <div className="flex items-center gap-1"><Wallet className="w-3 h-3" /> ₹{o.monthly_fee?.toLocaleString?.()} / {o.fee_type}</div>
                            </div>
                            <button onClick={() => setEditing(o)} data-testid={`ongoing-edit-${o.id}`}
                                className="mt-3 w-full text-xs font-semibold border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 inline-flex items-center justify-center gap-1">
                                <Edit3 className="w-3 h-3" /> Edit / Manage
                            </button>
                            <button onClick={() => setViewingAttendance(o.id)} data-testid={`ongoing-attendance-${o.id}`}
                                className="mt-1.5 w-full text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 inline-flex items-center justify-center gap-1">
                                <ClipboardCheck className="w-3 h-3" /> View Attendance
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <EditOngoingModal ongoing={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }} />
            )}
            {viewingAttendance && (
                <AdminAttendanceViewer ongoingId={viewingAttendance}
                    onClose={() => setViewingAttendance(null)} />
            )}
        </div>
    );
}

function EditOngoingModal({ ongoing, onClose, onSaved }) {
    const [form, setForm] = useState({
        monthly_fee: ongoing.monthly_fee,
        fee_type: ongoing.fee_type,
        schedule: ongoing.schedule,
        status: ongoing.status,
        notes: ongoing.notes || "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/ongoing-tuitions/${ongoing.id}`, form);
            toast.success("Updated");
            onSaved();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" data-testid="ongoing-edit-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="bg-[#1E3A8A] text-white p-4 flex items-center justify-between">
                    <h3 className="font-bold text-base">Edit Tuition · {ongoing.student_name}</h3>
                    <button onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-3">
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700 mb-1 block">Monthly fee (₹)</span>
                        <input type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: parseFloat(e.target.value) || 0 })} data-testid="edit-ongoing-fee" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700 mb-1 block">Fee type</span>
                        <select value={form.fee_type} onChange={(e) => setForm({ ...form, fee_type: e.target.value })} data-testid="edit-ongoing-fee-type"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                            <option value="monthly">Monthly</option>
                            <option value="session_wise">Per session</option>
                            <option value="full_course">Full course</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700 mb-1 block">Schedule</span>
                        <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="edit-ongoing-schedule" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700 mb-1 block">Status</span>
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-ongoing-status"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700 mb-1 block">Notes</span>
                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="edit-ongoing-notes" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button onClick={onClose} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
                        <button onClick={save} disabled={busy} data-testid="edit-ongoing-save"
                            className="flex-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
